// The `help` interface: help topics that explain how something on screen is
// worked out. A plugin registers its topics (`getApi('help')`, then
// `register(id, { title, body })`) and renders `HelpButton` (`./react`) with
// the topic's id wherever the "?" belongs; the button asks for the topic
// through a DOM event, so a presentational card needs no API. One overlay,
// hosted by the plugin `shared-help`, looks the topic up and shows it.
import type { ReactNode } from 'react';

export const HELP_API = 'help';
export const HELP_VERSION = '1.0.0';

/** The event a `HelpButton` dispatches on `window` to open a topic. */
export const HELP_OPEN_EVENT = 'chess:help-open';

export interface HelpTopic {
  title: string;
  /** The explanation: plain React content (paragraphs, lists), styled by the overlay. */
  body: ReactNode;
}

export interface HelpOpenDetail {
  id: string;
  /** The element that asked, which gets the focus back when the overlay closes. */
  opener?: HTMLElement | null;
}

export interface Help {
  /** Registers (or replaces) a topic; returns its removal. */
  register(id: string, topic: HelpTopic): () => void;
  /** The topic registered under `id`, if any. */
  topic(id: string): HelpTopic | undefined;
  /** Hears every registration and removal. Returns the unsubscribe. */
  subscribe(listener: () => void): () => void;
}

/** Help topics in memory: the provider `shared-help` registers one as `HELP_API`. */
export class HelpRegistry implements Help {
  private readonly topics = new Map<string, HelpTopic>();
  private readonly listeners = new Set<() => void>();

  register(id: string, topic: HelpTopic): () => void {
    this.topics.set(id, topic);
    this.emit();
    return () => {
      if (this.topics.get(id) !== topic) return; // replaced since: leave the newer one
      this.topics.delete(id);
      this.emit();
    };
  }

  topic(id: string): HelpTopic | undefined {
    return this.topics.get(id);
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private emit(): void {
    for (const l of [...this.listeners]) l();
  }
}

/** Asks the overlay to open a topic; what `HelpButton` does on a click. */
export function openHelp(id: string, opener?: HTMLElement | null): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent<HelpOpenDetail>(HELP_OPEN_EVENT, { detail: { id, opener } }));
}
